import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ALL_BADGES, RARITY_CONFIG, type BadgeDefinition } from "@/lib/badges";
import { motion } from "framer-motion";
import { Trophy, Lock, Clock, Lightbulb, Sparkles, Star, Gift } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { StudentEmptyState } from "@/components/StudentEmptyState";
import { BadgeArt } from "@/components/BadgeArt";

interface UserAchievement {
  id: string;
  userId: string;
  achievementType: string;
  achievementData: any;
  earnedAt: string;
}

interface WeeklyBadgeDraw {
  campaignName: string;
  prizeAmountJMD: number;
  target: number;
  earned: number;
  entered: boolean;
  drawAt: string;
}

const CATEGORIES = [
  { id: 'all',        label: 'All Badges' },
  { id: 'milestone',  label: 'Milestones' },
  { id: 'streak',     label: 'Streaks' },
  { id: 'mastery',    label: 'Mastery' },
  { id: 'scholar',    label: 'Scholar' },
  { id: 'social',     label: 'Social' },
  { id: 'dedication', label: 'Dedication' },
  { id: 'review',     label: 'Revision' },
];

function BadgeCard({ badge, achievement }: { badge: BadgeDefinition; achievement?: UserAchievement }) {
  const isEarned = !!achievement;
  const rarity = RARITY_CONFIG[badge.rarity];

  return (
    <Card
      className={[
        'relative overflow-hidden transition-all duration-300',
        isEarned
          ? `border ${rarity.borderColor} shadow-sm hover:shadow-md hover:-translate-y-0.5`
          : 'opacity-70',
      ].join(' ')}
      data-testid={`badge-card-${badge.type}`}
    >
      <CardContent className="p-5">
        <div className={`text-xs font-semibold uppercase tracking-wider mb-3 ${rarity.textColor}`}>
          {rarity.label}
        </div>

        <BadgeArt type={badge.type} name={badge.name} earned={isEarned} size={84} className="mx-auto mb-3" />

        <div className="text-center space-y-1">
          <h3
            className={`font-semibold text-sm leading-tight ${isEarned ? '' : 'text-muted-foreground'}`}
            data-testid={`badge-name-${badge.type}`}
          >
            {badge.name}
          </h3>
          <p className="text-xs text-muted-foreground leading-snug" data-testid={`badge-description-${badge.type}`}>
            {badge.description}
          </p>

          <div className="pt-2 space-y-1">
            {isEarned ? (
              <>
                <Badge
                  variant="default"
                  className="bg-green-500 text-white text-xs"
                  data-testid={`badge-status-earned-${badge.type}`}
                >
                  Earned
                </Badge>
                {achievement?.earnedAt && (
                  <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDistanceToNow(new Date(achievement.earnedAt), { addSuffix: true })}
                  </p>
                )}
              </>
            ) : (
              <>
                <Badge variant="secondary" className="text-xs" data-testid={`badge-status-locked-${badge.type}`}>
                  Locked
                </Badge>
                <p className="text-xs text-muted-foreground flex items-center justify-center gap-1 leading-snug">
                  <Lightbulb className="w-3 h-3 shrink-0" />
                  {badge.hint}
                </p>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ViewAllBadges() {
  const { user } = useAuth();

  const { data: userAchievements = [] } = useQuery<UserAchievement[]>({
    queryKey: ['/api/achievements'],
    enabled: !!user,
  });

  const { data: weeklyDraw } = useQuery<WeeklyBadgeDraw | null>({
    queryKey: ['/api/prizes/weekly-draw'],
    enabled: !!user,
  });
  const drawDay = weeklyDraw
    ? new Date(weeklyDraw.drawAt).toLocaleDateString('en-US', { weekday: 'long', timeZone: 'America/Jamaica' })
    : '';

  const achievementMap = new Map(
    userAchievements.map(a => [a.achievementType, a])
  );

  const earnedCount  = achievementMap.size;
  const totalCount   = ALL_BADGES.length;
  const progressPct  = totalCount > 0 ? (earnedCount / totalCount) * 100 : 0;

  // Earned badges sorted newest-first
  const earnedBadges = [...userAchievements]
    .sort((a, b) => new Date(b.earnedAt).getTime() - new Date(a.earnedAt).getTime())
    .map(a => ALL_BADGES.find(b => b.type === a.achievementType))
    .filter(Boolean) as BadgeDefinition[];

  // Recently earned (last 3) for the spotlight row
  const recentlyEarned = earnedBadges.slice(0, 3);

  // Read ?tab= from URL to auto-select tab (e.g. from badge notifications)
  const urlTab = new URLSearchParams(window.location.search).get('tab');
  const defaultTab = urlTab === 'earned' ? 'earned' : 'all';

  const renderGrid = (badges: BadgeDefinition[]) => (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
      {badges.map((badge, i) => (
        <motion.div
          key={badge.type}
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.25, delay: i * 0.02 }}
        >
          <BadgeCard badge={badge} achievement={achievementMap.get(badge.type)} />
        </motion.div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 py-8 px-4">
      <div className="container max-w-7xl mx-auto space-y-6">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="text-center space-y-1"
        >
          <div className="flex items-center justify-center gap-3">
            <Trophy className="w-9 h-9 text-primary" />
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
              Badge Collection
            </h1>
          </div>
          <p className="text-muted-foreground">
            Earn badges by completing challenges and reaching milestones
          </p>
        </motion.div>

        {/* Weekly badge draw */}
        {weeklyDraw && (
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: 0.03 }}
          >
            <Card className="border-primary/40 bg-primary/5" data-testid="card-weekly-draw">
              <CardContent className="p-5 flex items-start gap-4">
                <div className="shrink-0 w-11 h-11 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                  <Gift className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0 space-y-2">
                  <p className="font-semibold leading-tight" data-testid="text-weekly-draw-title">
                    {weeklyDraw.entered
                      ? `You are in ${drawDay}'s ${weeklyDraw.campaignName} draw!`
                      : weeklyDraw.campaignName}
                  </p>
                  <p className="text-sm text-muted-foreground leading-snug" data-testid="text-weekly-draw-line">
                    {weeklyDraw.entered
                      ? `You earned ${weeklyDraw.earned} new badges this week, so you could win a J$${weeklyDraw.prizeAmountJMD} gift card. Good luck!`
                      : `Earn ${weeklyDraw.target} new badges before ${drawDay} and you are in the draw for a J$${weeklyDraw.prizeAmountJMD} gift card.`}
                  </p>
                  {!weeklyDraw.entered && (
                    <div className="flex items-center gap-3">
                      <div className="relative h-2 flex-1 max-w-xs bg-muted rounded-full overflow-hidden">
                        <div
                          className="absolute inset-y-0 left-0 bg-primary rounded-full"
                          style={{ width: `${Math.min(100, (weeklyDraw.earned / weeklyDraw.target) * 100)}%` }}
                        />
                      </div>
                      <span className="text-xs font-semibold" data-testid="text-weekly-draw-progress">
                        {weeklyDraw.earned} of {weeklyDraw.target} this week
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Progress card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, delay: 0.05 }}
        >
          <Card data-testid="card-badge-progress">
            <CardContent className="p-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="text-2xl font-bold">{earnedCount} / {totalCount}</p>
                    <p className="text-sm text-muted-foreground">Badges Earned</p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-primary">{Math.round(progressPct)}%</p>
                    <p className="text-sm text-muted-foreground">Complete</p>
                  </div>
                </div>
                <div className="relative h-3 bg-muted rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progressPct}%` }}
                    transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
                    style={{ position: 'absolute', top: 0, bottom: 0, left: 0 }}
                    className="bg-primary rounded-full"
                  />
                </div>
                <div className="flex flex-wrap gap-3 pt-1">
                  {(['common','rare','epic','legendary'] as const).map(r => {
                    const cfg = RARITY_CONFIG[r];
                    const total   = ALL_BADGES.filter(b => b.rarity === r).length;
                    const earned  = ALL_BADGES.filter(b => b.rarity === r && achievementMap.has(b.type)).length;
                    return (
                      <div key={r} className="flex items-center gap-1.5">
                        <span className={`text-xs font-semibold ${cfg.textColor}`}>{cfg.label}</span>
                        <span className="text-xs text-muted-foreground">{earned}/{total}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Recently earned spotlight */}
        {recentlyEarned.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  Recently Earned
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {recentlyEarned.map(badge => (
                    <BadgeCard
                      key={badge.type}
                      badge={badge}
                      achievement={achievementMap.get(badge.type)}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Badge tabs */}
        <Card data-testid="card-badges">
          <CardHeader>
            <CardTitle className="text-xl">Your Badges</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue={defaultTab} className="w-full">
              <TabsList className="w-full justify-start flex-wrap h-auto gap-1.5 bg-transparent p-0 mb-6">

                {/* My Badges: earned only */}
                <TabsTrigger
                  value="earned"
                  className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                  data-testid="tab-earned"
                >
                  <Star className="w-3.5 h-3.5 mr-1" />
                  My Badges
                  <Badge variant="secondary" className="ml-1.5 text-xs">
                    {earnedCount}
                  </Badge>
                </TabsTrigger>

                {/* Category tabs */}
                {CATEGORIES.map(cat => {
                  const catBadges = cat.id === 'all'
                    ? ALL_BADGES
                    : ALL_BADGES.filter(b => b.category === cat.id);
                  const catEarned = catBadges.filter(b => achievementMap.has(b.type)).length;
                  return (
                    <TabsTrigger
                      key={cat.id}
                      value={cat.id}
                      className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                      data-testid={`tab-${cat.id}`}
                    >
                      {cat.label}
                      <Badge variant="secondary" className="ml-1.5 text-xs">
                        {catEarned}/{catBadges.length}
                      </Badge>
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {/* My Badges content: newest first */}
              <TabsContent value="earned" className="mt-0">
                {earnedBadges.length === 0 ? (
                  <StudentEmptyState
                    illustration="owl-lock"
                    title="No badges yet"
                    hint="Answer your first questions and Owlbert unlocks the first one."
                  />
                ) : (
                  renderGrid(earnedBadges)
                )}
              </TabsContent>

              {/* All + category contents */}
              <TabsContent value="all" className="mt-0">
                {renderGrid(ALL_BADGES)}
              </TabsContent>
              {CATEGORIES.slice(1).map(cat => (
                <TabsContent key={cat.id} value={cat.id} className="mt-0">
                  {renderGrid(ALL_BADGES.filter(b => b.category === cat.id))}
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
